import React, { useRef, useState } from "react";
import { FileUp, Loader2, UploadCloud, Video } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import { procedureRecordingsAPI } from "../services/api";
import ProcedureBotAvailabilityField from "./ProcedureBotAvailabilityField";

const ACCEPTED_VIDEO_TYPES = ".mp4,.mov,.m4v,.webm,.avi,.mkv,video/*";

const titleFromFilename = (filename = "") =>
  filename.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim().slice(0, 160);

const getVideoDuration = (file) =>
  new Promise((resolve) => {
    const sourceUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    const finish = (duration = 0) => {
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(sourceUrl);
      resolve(Number.isFinite(duration) && duration > 0 ? Math.round(duration) : 0);
    };

    video.preload = "metadata";
    video.onloadedmetadata = () => finish(video.duration);
    video.onerror = () => finish(0);
    video.src = sourceUrl;
  });

export default function ProcedureVideoUploadDialog({ onUploaded }) {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [sharedWithTeam, setSharedWithTeam] = useState(true);
  const [availableInBot, setAvailableInBot] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [error, setError] = useState("");

  const resetForm = () => {
    setFile(null);
    setTitle("");
    setDescription("");
    setDurationSeconds(0);
    setSharedWithTeam(true);
    setAvailableInBot(false);
    setUploadPercent(0);
    setUploadPhase("");
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOpenChange = (nextOpen) => {
    if (isUploading) return;
    setOpen(nextOpen);
    if (!nextOpen) resetForm();
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setError("");
    setDurationSeconds(0);
    if (!selectedFile) return;

    setTitle((currentTitle) => currentTitle || titleFromFilename(selectedFile.name));
    const detectedDuration = await getVideoDuration(selectedFile);
    setDurationSeconds(detectedDuration);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) {
      setError("Escolha um vídeo para enviar.");
      return;
    }
    if (!title.trim()) {
      setError("Informe um título para localizar o procedimento depois.");
      return;
    }

    setError("");
    setUploadPercent(0);
    setUploadPhase("Enviando vídeo...");
    setIsUploading(true);
    try {
      const response = await procedureRecordingsAPI.importToLibrary(
        {
          file,
          title: title.trim(),
          description: description.trim(),
          durationSeconds,
          sharedWithTeam,
          availableInBot,
        },
        (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total));
          setUploadPercent(percent);
          if (percent === 100) setUploadPhase("Convertendo para MP4 compatível...");
        },
      );
      onUploaded?.(response.data);
      toast({
        title: "Vídeo adicionado à biblioteca",
        description: "O procedimento foi convertido para MP4 e já pode ser reutilizado.",
      });
      resetForm();
      setOpen(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Não foi possível importar este vídeo.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" className="bg-violet-600 hover:bg-violet-700 text-white">
          <FileUp className="mr-2 h-4 w-4" /> Adicionar vídeo
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] overflow-y-auto rounded-xl sm:max-w-lg"
        onEscapeKeyDown={(event) => {
          if (isUploading) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isUploading) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UploadCloud className="h-5 w-5 text-violet-600" /> Adicionar vídeo à biblioteca</DialogTitle>
          <DialogDescription>Envie um procedimento já gravado. Ele será salvo na biblioteca e convertido para MP4 compatível com WhatsApp, iPhone e Android.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="procedure-video-file">Arquivo de vídeo</Label>
            <Input
              id="procedure-video-file"
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_VIDEO_TYPES}
              onChange={handleFileChange}
              disabled={isUploading}
              className="cursor-pointer"
            />
            <p className="text-xs text-gray-500">MP4, MOV, M4V, WebM, AVI ou MKV. O limite padrão é 2 GB por vídeo.</p>
          </div>

          {file && (
            <div className="flex items-center gap-3 rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <Video className="h-5 w-5 shrink-0 text-violet-600" />
              <span className="min-w-0 truncate">{file.name}</span>
              <span className="ml-auto shrink-0 text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="procedure-video-title">Título</Label>
            <Input id="procedure-video-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} disabled={isUploading} placeholder="Ex.: Emitir nota fiscal no MasterVendas" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="procedure-video-description">Descrição</Label>
            <Textarea id="procedure-video-description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={4000} disabled={isUploading} placeholder="Explique quando este procedimento deve ser usado." rows={3} />
          </div>

          <label className="flex items-start gap-3 rounded-lg border p-3 text-sm cursor-pointer hover:bg-gray-50">
            <input type="checkbox" checked={sharedWithTeam} onChange={(event) => setSharedWithTeam(event.target.checked)} disabled={isUploading} className="mt-0.5 h-4 w-4 accent-violet-600" />
            <span><strong>Disponibilizar para a equipe</strong><span className="block text-gray-500">Desmarque para restringir o acesso interno a você e aos administradores.</span></span>
          </label>
          <ProcedureBotAvailabilityField checked={availableInBot} onChange={setAvailableInBot} disabled={isUploading} />

          {isUploading && (
            <div className="space-y-2 rounded-lg border border-violet-100 bg-violet-50 p-3">
              <div className="flex items-center gap-2 text-sm text-violet-900"><Loader2 className="h-4 w-4 animate-spin" /> {uploadPhase || "Preparando..."}</div>
              <div className="h-2 overflow-hidden rounded-full bg-violet-200"><div className="h-full bg-violet-600 transition-all" style={{ width: `${uploadPercent}%` }} /></div>
              <p className="text-xs text-violet-800">Mantenha esta tela aberta até a conversão terminar.</p>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isUploading}>Cancelar</Button>
            <Button type="submit" disabled={isUploading} className="bg-violet-600 hover:bg-violet-700">
              {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
              {isUploading ? "Enviando..." : "Adicionar à biblioteca"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
